import { useState, useEffect, useRef } from 'react';
import { useSpecialties } from '../../../hooks/useDirectory';
import LocationFilter from './LocationFilter';
import type { SelectedTerritory } from './LocationFilter';
import type { DirectoryCountryLevel } from '../../../types/directory.types';

interface SearchSectionProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  selectedSpecialty: string;
  setSelectedSpecialty: (value: string) => void;
  // Ubicación (F3E-3B): un solo control sobre el modelo territorial genérico.
  // Los selectores legacy Departamento + Municipio ya no existen en el Home.
  countryIso: string | null;
  countryName: string | null;
  countryLevels: DirectoryCountryLevel[];
  selectedTerritory: SelectedTerritory | null;
  setSelectedTerritory: (unit: SelectedTerritory | null) => void;
  onlineBookingOnly: boolean;
  setOnlineBookingOnly: (value: boolean) => void;
  onDoctorSelect?: (doctorId: string) => void;
}

export default function SearchSection({
  searchTerm,
  setSearchTerm,
  selectedSpecialty,
  setSelectedSpecialty,
  countryIso,
  countryName,
  countryLevels,
  selectedTerritory,
  setSelectedTerritory,
  onlineBookingOnly,
  setOnlineBookingOnly,
}: SearchSectionProps) {
  const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const filtersContainerRef = useRef<HTMLDivElement>(null);

  // ─── DATOS REALES desde Supabase ───
  // Ya NO se descargan aquí `departments` ni el catálogo completo de
  // `municipalities`: el Home no vuelve a tocar los catálogos legacy. Siguen
  // vivos en LucyAdmin, afiliaciones y Mi Perfil, que son otros consumidores.
  const { data: specialties = [] } = useSpecialties();

  const handleSpecialtySelect = (specialtyId: string) => {
    setSelectedSpecialty(specialtyId);
    setShowSpecialtyDropdown(false);
  };

  const getSelectedSpecialtyName = () => {
    if (!selectedSpecialty) return 'Todas las especialidades';
    const spec = specialties.find(s => s.id === selectedSpecialty);
    return spec?.name || 'Todas las especialidades';
  };

  // Cerrar el dropdown de especialidad SOLO si el click es realmente fuera de
  // los filtros. (Antes el listener cerraba en cualquier mousedown, cancelando
  // los onClick de las opciones porque el botón se desmontaba antes del click.)
  // El control de Ubicación gestiona su propio cierre: es un diálogo, no un
  // dropdown, y en móvil ocupa la pantalla.
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (filtersContainerRef.current && !filtersContainerRef.current.contains(e.target as Node)) {
        setShowSpecialtyDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="space-y-4">
      {/* Toggle de Agenda Online - MUY VISIBLE */}
      <div className="bg-gradient-to-r from-brand-purple to-brand-purple-dark rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-5 border-2 border-brand-purple">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white rounded-full flex-shrink-0">
              <i className="ri-calendar-check-line text-xl sm:text-2xl text-brand-purple"></i>
            </div>
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-bold text-white">
                Solo médicos con agenda en línea
              </h3>
              <p className="text-xs sm:text-sm text-white">
                Reserva tu cita al instante
              </p>
            </div>
          </div>
          <button
            onClick={() => setOnlineBookingOnly(!onlineBookingOnly)}
            className={`relative w-14 h-8 sm:w-16 sm:h-9 rounded-full transition-all duration-300 cursor-pointer flex-shrink-0 ${
              onlineBookingOnly ? 'bg-white' : 'bg-white/30'
            }`}
          >
            <div
              className={`absolute top-1 left-1 w-6 h-6 sm:w-7 sm:h-7 rounded-full transition-all duration-300 ${
                onlineBookingOnly ? 'translate-x-6 sm:translate-x-7 bg-brand-purple' : 'translate-x-0 bg-gray-400'
              }`}
            >
              {onlineBookingOnly && (
                <i className="ri-check-line text-white text-sm sm:text-base absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></i>
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Buscador principal. TRES controles (antes cuatro): Buscar médico,
          Especialidad y Ubicación. En móvil son 3 filas apiladas en vez de 4,
          así que el bloque es más bajo que con Departamento + Municipio. */}
      <div ref={filtersContainerRef} className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-1.5 sm:p-2 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-1.5 sm:gap-2">
          {/* Search by Name */}
          <div className="relative">
            <div className="flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b md:border-b-0 md:border-r border-gray-200">
              <i className="ri-search-line text-xl sm:text-2xl text-brand-purple"></i>
              <div className="flex-1 min-w-0">
                <label className="block text-sm sm:text-base font-semibold text-gray-900 mb-1 sm:mb-1.5">
                  Buscar médico
                </label>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Nombre o apellido..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full text-sm sm:text-base text-gray-700 placeholder-gray-400 outline-none focus:outline-none"
                />
              </div>
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                  }}
                  className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line text-lg text-gray-400"></i>
                </button>
              )}
            </div>
          </div>

          {/* Specialty Filter */}
          <div className="relative">
            <button
              onClick={() => setShowSpecialtyDropdown(!showSpecialtyDropdown)}
              className="w-full flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b md:border-b-0 md:border-r border-gray-200 cursor-pointer text-left hover:bg-gray-50 transition-colors"
            >
              <i className="ri-stethoscope-line text-xl sm:text-2xl text-brand-purple"></i>
              <div className="flex-1 min-w-0">
                <label className="block text-sm sm:text-base font-semibold text-gray-900 mb-1 sm:mb-1.5">
                  Especialidad
                </label>
                <span className="text-sm sm:text-base text-gray-700 truncate block">
                  {getSelectedSpecialtyName()}
                </span>
              </div>
              <i className={`ri-arrow-down-s-line text-xl sm:text-2xl text-gray-400 transition-transform flex-shrink-0 ${showSpecialtyDropdown ? 'rotate-180' : ''}`}></i>
            </button>

            {showSpecialtyDropdown && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 max-h-60 sm:max-h-80 overflow-y-auto z-50">
                <button
                  onClick={() => handleSpecialtySelect('')}
                  className="w-full px-4 sm:px-6 py-2.5 sm:py-3 text-left text-sm sm:text-base text-gray-700 hover:bg-brand-mint/20 cursor-pointer whitespace-nowrap transition-colors"
                >
                  Todas las especialidades
                </button>
                {specialties.map((spec) => (
                  <button
                    key={spec.id}
                    onClick={() => handleSpecialtySelect(spec.id)}
                    className="w-full px-4 sm:px-6 py-2.5 sm:py-3 text-left text-sm sm:text-base text-gray-700 hover:bg-brand-mint/20 cursor-pointer whitespace-nowrap transition-colors"
                  >
                    {spec.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Ubicación (F3E-3B) — control único, progresivo y genérico */}
          <LocationFilter
            countryIso={countryIso}
            countryName={countryName}
            levels={countryLevels}
            selected={selectedTerritory}
            onSelect={setSelectedTerritory}
            disabled={!countryIso}
          />
        </div>
      </div>
    </div>
  );
}
